import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { confirm } from "@mhmo91/schmancy/overlay";
import { Order } from "@momo/types";
import { html } from "lit";
import { property, state } from "lit/decorators.js";
import { filter, takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import {
	orderEmailSettled$,
	orderFixEmailRequested$,
	orderResendRequested$,
} from "./order-ops.state.ts";

@schmancyElement("order-send-tickets")
export class OrderSendTickets extends LocalizeMixin(SchmancyElement) {
	@property() eventId = "";
	@property() orderId = "";
	@property({ attribute: false }) currentEmail = "";
	@property({ type: Boolean }) canSend = false;
	@property({ type: Boolean }) canCorrect = false;

	/** `null` until the operator types — so the field shows the address on
	 *  file without a lifecycle step copying the property into state. */
	@state() private draft: string | null = null;

	@state() private busy = false;
	@state() private outcome:
		| "queued"
		| "updated"
		| "sendRefused"
		| "refused"
		| null = null;
	private requestId: string | null = null;
	private correctedEmail: string | null = null;

	override connectedCallback() {
		super.connectedCallback();
		orderEmailSettled$
			.pipe(
				filter((answer) => answer.requestId === this.requestId),
				takeUntil(this.disconnecting),
			)
			.subscribe((answer) => {
				this.busy = false;
				this.outcome = answer.outcome;
				if (answer.outcome === "updated" || answer.outcome === "sendRefused")
					this.correctedEmail = this.draft?.trim() ?? this.currentEmail;
			});
	}

	override render() {
		const address = (this.draft ?? this.currentEmail).trim();
		const corrected =
			address.toLowerCase() !==
			(this.correctedEmail ?? this.currentEmail).trim().toLowerCase();
		// The SAME validator Order.buyerEmail enforces at read — this write goes
		// straight through updateDoc with no schema parse of its own, so a
		// looser hand-rolled regex here would let a value land that every
		// listener's own OrderSchema.safeParse then silently drops the order
		// for.
		const invalid =
			!Order.OrderSchema.shape.buyerEmail.safeParse(address).success;
		const blocked = !corrected && !this.canSend;
		return html`
			<div class="grid w-sm max-w-full gap-4 p-5">
				<schmancy-input
					label=${this.t(
						"admin.events.console.orderDetail.sendTicketsAddressLabel",
					)}
					type="email"
					required
					?readonly=${!this.canCorrect || this.busy}
					?error=${invalid}
					hint=${
						invalid
							? this.t(
									this.canCorrect
										? "admin.events.console.orderDetail.sendTicketsInvalidEmail"
										: "admin.events.console.orderDetail.sendTicketsNoAddress",
								)
							: ""
					}
					.value=${this.draft ?? this.currentEmail}
					@input=${(e: CustomEvent<{ value: string }>) => {
						if (e.detail.value === (this.draft ?? this.currentEmail)) return;
						this.draft = e.detail.value;
						this.outcome = null;
					}}
				></schmancy-input>

				<div class="flex justify-end gap-2">
					<schmancy-button
						variant="text"
                        ?disabled=${this.busy}
						@click=${() =>
							this.dispatchEvent(
								new CustomEvent("close", { bubbles: true, composed: true }),
							)}
					>
						${this.t("admin.events.console.orderDetail.sendTicketsCancel")}
					</schmancy-button>
					<schmancy-button
						variant="filled"
                        color=${this.outcome === "refused" || this.outcome === "sendRefused" ? "error" : "primary"}
						.busy=${this.busy}
                        ?disabled=${invalid || blocked || this.outcome === "queued" || this.outcome === "updated"}
						aria-label=${
							blocked
								? this.t(
										"admin.events.console.orderDetail.sendTicketsNoPermissionAria",
									)
								: this.t(
										this.outcome === "refused" || this.outcome === "sendRefused"
											? "admin.common.retrySend"
											: corrected
												? "admin.events.console.orderDetail.sendTicketsCorrectSubmit"
												: "admin.events.console.orderDetail.sendTicketsSubmit",
									)
						}
                        @click=${async () => {
													if (this.busy || invalid || blocked) return;
													this.busy = true;
													if (corrected) {
														const accepted = await confirm({
															title: this.t(
																"admin.events.console.orderDetail.sendTicketsConfirmTitle",
															),
															message: this.t(
																"admin.events.console.orderDetail.sendTicketsConfirmBody",
																{
																	newEmail: address,
																	oldEmail:
																		this.correctedEmail ?? this.currentEmail,
																},
															),
															confirmText: this.t(
																"admin.events.console.orderDetail.sendTicketsConfirmSubmit",
															),
															cancelText: this.t(
																"admin.events.console.orderDetail.sendTicketsConfirmCancel",
															),
															variant: "danger",
														});
														if (!accepted || !this.isConnected) {
															this.busy = false;
															return;
														}
													}
													this.requestId = crypto.randomUUID();
													if (corrected)
														orderFixEmailRequested$.next({
															requestId: this.requestId,
															eventId: this.eventId,
															orderId: this.orderId,
															newEmail: address,
														});
													else
														orderResendRequested$.next({
															requestId: this.requestId,
															eventId: this.eventId,
															orderId: this.orderId,
														});
												}}
					>
                        <span class="grid">
                            <span class="invisible col-start-1 row-start-1" aria-hidden="true">${this.t("admin.events.console.orderDetail.sendTicketsCorrectSubmit")}</span>
                            <span class="invisible col-start-1 row-start-1" aria-hidden="true">${this.t("admin.events.console.orderDetail.sendTicketsSubmit")}</span>
                            <span class="invisible col-start-1 row-start-1" aria-hidden="true">${this.t("admin.common.retrySend")}</span>
                            <span class="col-start-1 row-start-1">${this.t(this.outcome === "refused" || this.outcome === "sendRefused" ? "admin.common.retrySend" : corrected ? "admin.events.console.orderDetail.sendTicketsCorrectSubmit" : "admin.events.console.orderDetail.sendTicketsSubmit")}</span>
                        </span>
					</schmancy-button>
				</div>
                <schmancy-typography type="body" token="sm" role="status" class="h-12 text-pretty">
                  ${this.outcome === "queued" ? this.t("admin.events.console.orderDetail.toastEmailQueued") : this.outcome === "updated" ? this.t("admin.events.console.orderDetail.toastEmailUpdated", { email: address }) : this.outcome === "sendRefused" ? this.t("admin.events.console.orderDetail.sendTicketsAddressUpdatedSendFailed") : this.outcome === "refused" ? this.t("admin.events.console.orderDetail.sendTicketsFailed") : ""}
                </schmancy-typography>
            </div>
        `;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"order-send-tickets": OrderSendTickets;
	}
}
